import React, { useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import '../styles/status-cards.scss'
import Swal from 'sweetalert2'
import Modal from 'react-modal'

interface StatusCardsProps {
  doctorCount?: number
  patientCount?: number
  waitingCount?: number
  attendingCount?: number
  returningCount?: number
  // waitTime?: string
  viewMode: 'queue' | 'indicators'
  onViewModeChange: (mode: 'queue' | 'indicators') => void,
  corporations: object,
  onCorporationFilterChange?: (selectedCorporationIds: string[]) => void
  patientStatus: 'ONLINE' | 'OFFLINE' | 'BOTH'
  onPatientStatusChange: (status: 'ONLINE' | 'OFFLINE' | 'BOTH') => void
}

const StatusCards: React.FC<StatusCardsProps> = ({
  doctorCount = 0,
  patientCount = 0,
  waitingCount = 0,
  attendingCount = 0,
  returningCount = 0,
  // waitTime = '0:00',
  viewMode,
  onViewModeChange,
  corporations,
  onCorporationFilterChange,
  patientStatus = 'ONLINE',
  onPatientStatusChange,
}) => {
  const history = useHistory()
  const [selectedCorporations, setSelectedCorporations] = useState<string[]>([])
  const [isLogoutModal, setIsLogoutModal] = React.useState<boolean>(false)

      const customStyles = {
      content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
      },
    }
  const transformedCorporations = useMemo(() => {
    if (!corporations || !Array.isArray(corporations)) {
      return []
    }
    return corporations.map((item: any) => ({
      slug: item.slug,
      name: item.name
    }))
  }, [corporations])
  const logoutHandler = () => {
    setIsLogoutModal(false)
    localStorage.removeItem('liveMonitorUser')
    history.replace('/login')
  }
  const handleCorporationFilterChange = (selectedIds: string[]) => {
    setSelectedCorporations(selectedIds)
    if (onCorporationFilterChange) {
      onCorporationFilterChange(selectedIds)
    }
  }

  // Check which static filter is active
  const isAllSelected = selectedCorporations.length === 0
  
  const openCorporationFilterSelector = React.useCallback(async () => {
    if (!transformedCorporations || transformedCorporations.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nenhuma corporação disponível',
        text: 'Não há corporações para filtrar.'
      })
      return
    }
    const checkboxesHtml = transformedCorporations.map((corp: any) => {
      const isChecked = selectedCorporations.includes(corp.slug) ? 'checked' : ''
      const escapedSlug = String(corp.slug || '').replace(/"/g, '&quot;')
      return `
        <div style="text-align: left; margin: 10px 0;">
          <label style="display: flex; align-items: center; cursor: pointer;">
            <input type="checkbox" value="${escapedSlug}" ${isChecked} style="margin-right: 10px; cursor: pointer;" data-corp-slug="${escapedSlug}">
            <span>${corp.name}</span>
          </label>
        </div>
      `
    }).join('')

    const { value: formValues } = await Swal.fire({
      title: 'Filtro por Corporação',
      html: `
        <div style="max-height: 400px; overflow-y: auto; text-align: left;">
          <div style="margin-bottom: 15px;">
            <label style="display: flex; align-items: center; cursor: pointer;">
              <input type="checkbox" id="select-all" style="margin-right: 10px; cursor: pointer;">
              <strong>Todos</strong>
            </label>
          </div>
          ${checkboxesHtml}
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Filtrar',
      didOpen: () => {
        const selectAllCheckbox = document.getElementById('select-all') as HTMLInputElement
        const checkboxes = document.querySelectorAll('input[type="checkbox"]:not(#select-all)') as NodeListOf<HTMLInputElement>
        
        // When "Todos" checkbox is clicked, uncheck all corporation checkboxes
        selectAllCheckbox?.addEventListener('change', (e) => {
          if ((e.target as HTMLInputElement).checked) {
            // If "Todos" is checked, uncheck all corporation checkboxes
            checkboxes.forEach((cb) => {
              cb.checked = false
            })
          }
        })
        
        // When any corporation checkbox is checked, uncheck "Todos"
        checkboxes.forEach((cb) => {
          cb.addEventListener('change', () => {
            if (cb.checked && selectAllCheckbox) {
              selectAllCheckbox.checked = false
            }
          })
        })
        
        // Set initial state: "Todos" is checked if no corporations are selected
        if (selectAllCheckbox) {
          selectAllCheckbox.checked = selectedCorporations.length === 0
        }
      },
      preConfirm: () => {
        const selectAllCheckbox = document.getElementById('select-all') as HTMLInputElement
        if (selectAllCheckbox?.checked) {
          return []
        }
        
        // Get only the corporation checkboxes (exclude select-all) using data attribute for more reliable selection
        const checkboxes = document.querySelectorAll('input[type="checkbox"][data-corp-slug]:not(#select-all)') as NodeListOf<HTMLInputElement>
        const corporationSlugs = transformedCorporations.map((corp: any) => String(corp.slug || ''))
        const selected = Array.from(checkboxes)
          .filter(cb => {
            // Only include checked checkboxes with valid corporation slug values
            const value = cb.getAttribute('data-corp-slug') || cb.value
            return cb.checked && value && corporationSlugs.includes(value) && value !== 'on' && value !== '_all'
          })
          .map(cb => {
            // Use data attribute if available, otherwise fall back to value
            return cb.getAttribute('data-corp-slug') || cb.value
          })
          .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
        return selected
      }
    })

    if (formValues && Array.isArray(formValues)) {
      // If all selected or none selected, clear filter (show all)
      if (formValues.length === 0 || formValues.length === transformedCorporations.length) {
        handleCorporationFilterChange([])
      } else {
        handleCorporationFilterChange(formValues)
      }
    }
  }, [transformedCorporations, selectedCorporations, onCorporationFilterChange])

  return (
    <div className="status-cards-container">
      <div className="status-cards-row">
        {/* LEFT SIDE: Header + View Toggles */}
        <div className="status-cards-left">
          <div className="status-cards__header">
            <img src="/img/svg/shapes/icon.svg" alt="icon" className="status-cards__icon" />
            <div className="status-cards__text">
              <h4 className="status-cards__title">Painel de monitoramento</h4>
              <p className="status-cards__subtitle">Pronto Atendimento Virtual</p>
            </div>
          </div>

          <div className="status-cards-view-toggles">
            <button
              className={`view-toggle-btn ${viewMode === 'queue' ? 'active' : ''}`}
              onClick={() => onViewModeChange('queue')}
            >
              PAINEL DE FILA
            </button>

            <button
              className={`view-toggle-btn ${viewMode === 'indicators' ? 'active' : ''}`}
              onClick={() => onViewModeChange('indicators')}
            >
              INDICADORES
            </button>
          </div>

        </div>

        {/* RIGHT SIDE: Filter + Cards */}
        {viewMode === 'queue' && (
        <div className="status-cards-right">
          {/* Filter */}
          <div className="status-cards-filter">
            <span className="status-cards-filter__label">Filtrar por Corporação</span>
            <div className="status-cards-filter__buttons">
              <button
                className={`status-cards-filter__btn ${isAllSelected ? 'active' : ''}`}
                onClick={() => handleCorporationFilterChange([])}>
                Todos
              </button>
              {selectedCorporations.length > 0 && (
                <div className="status-cards-filter__tags">
                  {selectedCorporations.map((corpSlug, index) => {
                    return corpSlug ? (
                      <span 
                        key={`${corpSlug}_${index}`}
                        className="status-cards-filter__tag"
                      >
                        {corpSlug}
                        <button
                          className="status-cards-filter__tag-remove"
                          onClick={(e) => {
                            e.stopPropagation()
                            const newSelected = selectedCorporations.filter(id => id !== corpSlug)
                            handleCorporationFilterChange(newSelected)
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ) : null
                  })}
                </div>
              )}              
              <a className="btn btn-sm btn-white btn-icon-only rounded-circle ml-4" onClick={openCorporationFilterSelector}>
                <span className="btn-inner--icon">
                  <i className="fas fa-filter"></i>
                </span>
              </a>
              <button
                onClick={() =>  onPatientStatusChange('ONLINE')}
                className={`status-cards-filter__btn ${patientStatus === 'ONLINE' ? 'active' : ''}`}
                title="Show online patients"
              >
                ONLINE
              </button>
              <button
                onClick={() => onPatientStatusChange('OFFLINE')}
                className={`status-cards-filter__btn ${patientStatus === 'OFFLINE' ? 'active' : ''}`}
                title="Show offline patients"
              >
                OFFLINE
              </button>
              <button
                onClick={() => onPatientStatusChange('BOTH')}
                className={`status-cards-filter__btn ${patientStatus === 'BOTH' ? 'active' : ''}`}
                title="Show both online and offline patients"
              >
                AMBOS
              </button>
              <button
                onClick={() => setIsLogoutModal(true)}
                className="status-cards-filter__btn"
                title="Sair do sistema"
              >
                Sair
              </button>
            </div>
          </div>

          {/* Status Cards */}
          <div className="status-cards__list">
            {/* Online Card */}
            <div className="status-card status-card--small">
              <div className="status-left">
                <div className="status-dot online" />
                <div className="status-title">Online</div>
              </div>
              <div className="status-right">
                <div className="status-item">
                  <i className="fas fa-user" />
                  <div className="status-num">{patientCount}</div>
                </div>
                <div className="status-item">
                  <i className="fas fa-stethoscope" />
                  <div className="status-num">{doctorCount}</div>
                </div>
              </div>
            </div>

            {/* Waiting */}
            <div className="status-card status-card--small">
              <div className="status-left">
                <div className="status-dot waiting" />
                <div className="status-title">Em Espera</div>
              </div>
              <div className="status-right">
                <div className="status-num big">{waitingCount}</div>
              </div>
            </div>

            {/* Attending */}
            <div className="status-card status-card--small">
              <div className="status-left">
                <div className="status-dot attending" />
                <div className="status-title">Em Atendimento</div>
              </div>
              <div className="status-right">
                <div className="status-num big">{attendingCount}</div>
              </div>
            </div>

            {/* Returning */}
            <div className="status-card status-card--small">
              <div className="status-left">
                <div className="status-dot returning" />
                <div className="status-title">Em Retorno</div>
              </div>
              <div className="status-right">
                <div className="status-num big">{returningCount}</div>
              </div>
            </div>

            {/* Wait Time */}
            {/* <div className="status-card status-card--end">
              <div className="status-left">
                <div className="status-title">Tempo de Espera</div>
              </div>
              <div className="status-right">
                <div className="status-num big">{waitTime}</div>
              </div>
            </div> */}
          </div>
        </div>
        )}
      </div>
      <Modal 
        isOpen={isLogoutModal} 
        onRequestClose={() => setIsLogoutModal(false)}
        style={customStyles}
        ariaHideApp={false}
      >
        <div className="logout-modal d-flex flex-column align-items-center">
          <h5 className='font-weight'>Tem certeza de que deseja sair?</h5>
          <div className='btn-wrapper d-flex mt-3'>
            <button type="button" className="btn custom-btn-primary" onClick={logoutHandler}>
              Sim
            </button>
            <button type="button" className="btn custom-btn-danger" onClick={() => setIsLogoutModal(false)}>
              Cancelar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default StatusCards
